import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => n || 0;

function StatCard({ label, value, color='#556B2F', bg='#F0F7F1', icon, onClick }) {
  return (
    <div onClick={onClick}
      style={{background:bg, border:`1px solid ${color}22`, borderRadius:12,
        padding:'18px 20px', cursor:onClick?'pointer':'default',
        transition:'all .2s'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',
            letterSpacing:'.1em',marginBottom:6}}>{label}</div>
          <div style={{fontSize:28,fontWeight:700,color,fontFamily:'Cormorant Garamond,serif'}}>
            {value}
          </div>
        </div>
        {icon && <div style={{fontSize:24,opacity:.6}}>{icon}</div>}
      </div>
    </div>
  );
}

export default function BrandContentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [oversight, setOversight] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = user?.role === 'super_admin';
  const isManager = user?.role === 'sales_manager';
  const isBCO = user?.role === 'brand_content_officer';

  useEffect(() => {
    const calls = [get('/bc/dashboard')];
    if (isSuperAdmin || isManager) calls.push(get('/bc/dashboard/oversight'));
    Promise.all(calls).then(([d, o]) => {
      setData(d);
      if (o) setOversight(o);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page"><div className="spinner"/></div>;

  return (
    <div className="page">
      {/* Header */}
      <div style={{marginBottom:28}}>
        <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',
          letterSpacing:'.12em',marginBottom:4}}>Brand & Content</div>
        <h1 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2rem',
          color:'#2C1F16',margin:'0 0 4px'}}>
          {isBCO ? `Good ${getGreeting()}, ${user?.name?.split(' ')[0]}` : 'Brand & Content Overview'}
        </h1>
        <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>
          {new Date().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
        </p>
      </div>

      {/* BCO Dashboard */}
      {(isBCO || isSuperAdmin) && data && (
        <>
          {/* Alert row */}
          {(data.overdue_content > 0 || data.awaiting_approval > 0) && (
            <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
              {data.overdue_content > 0 && (
                <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',
                  borderRadius:10,padding:'10px 16px',fontSize:12,color:'#8F2E24',
                  display:'flex',alignItems:'center',gap:8}}>
                  ⚠ <strong>{data.overdue_content}</strong> overdue content item{data.overdue_content!==1?'s':''}
                </div>
              )}
              {data.awaiting_approval > 0 && (
                <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',
                  borderRadius:10,padding:'10px 16px',fontSize:12,color:'#8B5E0A',
                  display:'flex',alignItems:'center',gap:8}}>
                  ⏳ <strong>{data.awaiting_approval}</strong> item{data.awaiting_approval!==1?'s':''} awaiting approval
                </div>
              )}
              {!data.report_submitted && (
                <div style={{background:'#F0F0FF',border:'1px solid #C0C0FF',
                  borderRadius:10,padding:'10px 16px',fontSize:12,color:'#4040AA',
                  display:'flex',alignItems:'center',gap:8}}>
                  📋 Daily report not yet submitted
                </div>
              )}
            </div>
          )}

          {/* Stat cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',
            gap:12,marginBottom:24}}>
            <StatCard label="Due Today" value={fmt(data.today_content)}
              color='#556B2F' bg='#F0F7F1' icon='📅'
              onClick={()=>navigate('/bc-content?filter=today')} />
            <StatCard label="Overdue" value={fmt(data.overdue_content)}
              color={data.overdue_content>0?'#8F2E24':'#556B2F'}
              bg={data.overdue_content>0?'#FDEAEA':'#F0F7F1'} icon='⚠'
              onClick={()=>navigate('/bc-content?filter=overdue')} />
            <StatCard label="Awaiting Approval" value={fmt(data.awaiting_approval)}
              color='#8B5E0A' bg='#FFF3DC' icon='⏳'
              onClick={()=>navigate('/bc-content?filter=pending_approval')} />
            <StatCard label="Open Tasks" value={fmt(data.open_tasks)}
              color='#3B2A21' bg='#F9F8F5' icon='✅'
              onClick={()=>navigate('/tasks')} />
            <StatCard label="Draft Blogs" value={fmt(data.draft_blogs)}
              color='#556B2F' bg='#F0F7F1' icon='✍️'
              onClick={()=>navigate('/bc-blog')} />
            <StatCard label="Listing Issues" value={fmt(data.listing_issues)}
              color={data.listing_issues>0?'#8B5E0A':'#556B2F'}
              bg={data.listing_issues>0?'#FFF3DC':'#F0F7F1'} icon='🌐'
              onClick={()=>navigate('/bc-website')} />
            <StatCard label="Active Campaigns" value={fmt(data.active_campaigns)}
              color='#A8893D' bg='#F9F4E8' icon='🚀'
              onClick={()=>navigate('/bc-campaigns')} />
            <StatCard label="Resources" value={fmt(data.active_resources)}
              color='#3B2A21' bg='#F9F8F5' icon='📦'
              onClick={()=>navigate('/bc-resources')} />
          </div>

          {/* Upcoming shoots */}
          {data.upcoming_shoots?.length > 0 && (
            <div style={{background:'#fff',border:'1px solid #E8E0D4',
              borderRadius:12,padding:20,marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>
                📸 Upcoming Shoots
              </div>
              {data.upcoming_shoots.map(s=>(
                <div key={s.id} style={{display:'flex',justifyContent:'space-between',
                  alignItems:'center',padding:'10px 0',borderBottom:'1px solid #F2EFE7'}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'#2C1F16'}}>{s.title}</div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>
                      {s.required_products||'No products specified'}
                    </div>
                  </div>
                  <div style={{fontSize:12,fontWeight:600,color:'#A8893D'}}>
                    {new Date(s.shoot_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Daily report status */}
          <div style={{background:data.report_submitted?'#F0F7F1':'#FFF3DC',
            border:`1px solid ${data.report_submitted?'#B8DEC0':'#F0D9A0'}`,
            borderRadius:12,padding:16,display:'flex',
            justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:12,fontWeight:600,
                color:data.report_submitted?'#2E6B3A':'#8B5E0A'}}>
                {data.report_submitted ? '✅ Daily Report Submitted' : '⏳ Daily Report Pending'}
              </div>
              <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                {data.report_submitted ? 'Your report for today has been submitted.' : 'Remember to submit your daily report before close of business.'}
              </div>
            </div>
            {!data.report_submitted && (
              <button className="btn btn-primary btn-sm"
                onClick={()=>navigate('/team-reports')}>
                Submit Now
              </button>
            )}
          </div>
        </>
      )}

      {/* Manager Oversight */}
      {(isSuperAdmin || isManager) && oversight && (
        <div style={{marginTop:isSuperAdmin?32:0}}>
          <div style={{fontSize:11,fontWeight:700,color:'#3B2A21',
            textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>
            Brand & Content Oversight
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12}}>
            <StatCard label="Content Scheduled" value={fmt(oversight.content_scheduled)}
              color='#556B2F' bg='#F0F7F1' icon='📅' onClick={()=>navigate('/bc-content')} />
            <StatCard label="Content Overdue" value={fmt(oversight.content_overdue)}
              color={oversight.content_overdue>0?'#8F2E24':'#556B2F'}
              bg={oversight.content_overdue>0?'#FDEAEA':'#F0F7F1'} icon='⚠' />
            <StatCard label="Awaiting Approval" value={fmt(oversight.awaiting_approval)}
              color='#8B5E0A' bg='#FFF3DC' icon='⏳'
              onClick={()=>navigate('/bc-content?filter=pending_approval')} />
            <StatCard label="Active Campaigns" value={fmt(oversight.active_campaigns)}
              color='#A8893D' bg='#F9F4E8' icon='🚀'
              onClick={()=>navigate('/bc-campaigns')} />
            <StatCard label="Listing Issues" value={fmt(oversight.listing_issues)}
              color={oversight.listing_issues>0?'#8B5E0A':'#556B2F'}
              bg={oversight.listing_issues>0?'#FFF3DC':'#F0F7F1'} icon='🌐'
              onClick={()=>navigate('/bc-website')} />
            <StatCard label="Blogs Pending" value={fmt(oversight.blogs_pending)}
              color='#556B2F' bg='#F0F7F1' icon='✍️'
              onClick={()=>navigate('/bc-blog')} />
          </div>
        </div>
      )}
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
}
